import * as signalR from "@microsoft/signalr";
import { createContext, useContext, useEffect, useState } from "react";
import { baseUrl } from "./Fetch";
import { IConversation, ITagReq, IUser } from "../components/jvConnect/modal";

type NotificationContextType = {
    connection: signalR.HubConnection | null;
    setUsersFromArray: (users: IUser[]) => void;
    getUserById: (id: number) => IUser | undefined;
    searchUsers: (query: string) => IUser[];
    isOnline: (userId: number) => boolean;
    updateOnlineUsers: (
        type: "set" | "add" | "remove",
        userIdOrList: number | number[]
    ) => void;

    conversations: IConversation[];
    setConversations: React.Dispatch<React.SetStateAction<IConversation[]>>;
    currentConversationId?: number;
    setCurrentConversationId: React.Dispatch<React.SetStateAction<number | undefined>>;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onUserSelect: (user: IUser) => void;
    req: ITagReq | undefined,
    setReq: React.Dispatch<React.SetStateAction<ITagReq | undefined>>
};

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider = ({ children }: any) => {
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
    const [onlines, setOnlines] = useState<Set<number>>(new Set());
    const [users, setUsers] = useState<Record<number, IUser>>({});

    const [conversations, setConversations] = useState<IConversation[]>([]);
    const [currentConversationId, setCurrentConversationId] = useState<number>();
    const [open, setOpen] = useState(false);
    const [req, setReq] = useState<ITagReq>();

    useEffect(() => {
        const newConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${baseUrl.split('/api/')[0]}/hubs/notification`, {
                accessTokenFactory: () =>
                    JSON.parse(sessionStorage.getItem('auth') + "")?.token
            })
            .withAutomaticReconnect()
            .build();

        newConnection.start()
            .then(() => console.log("SignalR Connected"))
            .catch(err => console.error("SignalR Error:", err));

        setConnection(newConnection);

        return () => {
            newConnection.stop();
        };
    }, []);

    const setUsersFromArray = (userList: IUser[]) => {
        const dict: Record<number, IUser> = {};
        userList.forEach(u => {
            dict[u.id] = u;
        });
        setUsers(dict);
    };

    const getUserById = (id: number): IUser | undefined => users[id];

    const searchUsers = (query: string): IUser[] => {
        if (!query) return [];

        const lower = query.toLowerCase();

        return Object.values(users)
            .filter(u => u.name?.toLowerCase().includes(lower))
            .slice(0, 10);
    };

    const updateOnlineUsers = (
        type: "set" | "add" | "remove",
        userIdOrList: number | number[]
    ) => {
        setOnlines(prev => {
            const newSet = new Set(prev);

            switch (type) {
                case "set":
                    return new Set(userIdOrList as number[]);

                case "add":
                    newSet.add(userIdOrList as number);
                    return newSet;

                case "remove":
                    newSet.delete(userIdOrList as number);
                    return newSet;

                default:
                    return prev;
            }
        });
    };

    const isOnline = (userId: number): boolean => {
        return onlines.has(userId);
    };

    const onUserSelect = (user: IUser) => {
        const existing = conversations?.find(f => f.userId === user.id);
        if (existing) {
            setCurrentConversationId(existing.id);
        } else {
            const newConversation: IConversation =
                { id: 0, lastMessage: '', lastMessageTime: '', unreadCount: 0, user: user, userId: user.id, messages: [], isGroup: false };
            setConversations(prev => {
                const filtered = prev ? prev.filter(f => f.id > 0) : [];
                return [newConversation, ...filtered];
            });
            setCurrentConversationId(newConversation.id);
        }
        setOpen(true);
    }

    return (
        <NotificationContext.Provider value={{
            connection, setUsersFromArray, getUserById, searchUsers, isOnline, updateOnlineUsers, onUserSelect,
            conversations, setConversations, currentConversationId, setCurrentConversationId, open, setOpen,
            req, setReq
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = (): NotificationContextType => {
    const context = useContext(NotificationContext);

    if (!context) {
        throw new Error("useNotification must be used within NotificationProvider");
    }

    return context;
};